import { Stack } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Recommendation, TTDCampaign } from '@feathr/blackbox';
import { ERecommendationType } from '@feathr/blackbox';
import { Alert, Button, Modal } from '@feathr/components';
import { useCampaignValidation } from '@feathr/extender/App/EventsPage/CampaignEditPage/AdWizard';
import SaveCampaignButton from '@feathr/extender/App/EventsPage/CampaignEditPage/SaveCampaignButton';
import { useStore } from '@feathr/extender/state';
import { useUser } from '@feathr/report_components';

import AddGoal from './AddGoal';
import EnableOmnitargeting from './EnableOmnitargeting';
import ExtendEndDate from './ExtendEndDate';
import IncreaseBaseBid from './IncreaseBaseBid';
import IncreaseBudget from './IncreaseBudget';
import IncreaseBudgetExtendEndDate from './IncreaseBudgetExtendEndDate';
import IncreaseFrequencyCap from './IncreaseFrequencyCap';
import { updateRecommendationStatus } from './RecommendationModal.utils';

import * as styles from './RecommendationModal.css';

interface IProps {
  /** The Tradedesk campaign */
  campaign: TTDCampaign;
  /** Function to close the modal */
  onClose: () => void;
  /** Whether the modal is open */
  opened: boolean;
  /** Refreshes the recommendations collection to see if any recommendation statuses have changed */
  onRefreshCollection: () => Promise<void>;
  /** The recommendation model */
  recommendation: Recommendation;
}

/**
 * Composite component to display the recommendation alert
 * @param description - The description of the alert
 */
function RecommendationAlert({
  description,
}: Readonly<{ description: string | string[] }>): JSX.Element {
  const { t } = useTranslation();

  return (
    <Alert
      className={styles.alert}
      description={description}
      title={t('Based on Feathr Intelligence')}
      type={'recommendation'}
    />
  );
}

/**
 * Component to display the recommendation modal
 * @param campaign - The Tradedesk campaign
 * @param onClose - Function to close the modal
 * @param opened - Whether the modal is open
 * @param recommendation - The recommendation model
 * @param onRefreshCollection - Function to refresh the recommendations collection
 */
function RecommendationModal({
  campaign,
  onClose,
  opened,
  recommendation,
  onRefreshCollection,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const user = useUser();
  const { Goals } = useStore();
  const { childModels, grandchildModels, getValidationErrors } = useCampaignValidation({
    campaign,
  });

  const goals = Goals.list({
    filters: {
      _parent: campaign.id,
      is_archived__ne: true,
    },
  });

  const type = recommendation.get('type');
  const onPostSave = updateRecommendationStatus({
    type,
    campaign,
    recommendation,
    onRefreshCollection,
    timezone: user.get('timezone'),
    goals: goals.models,
  });
  const errors = getValidationErrors();

  async function handleClose(): Promise<void> {
    // Reset any unsaved changes to prevent them from displaying in the wizard
    await campaign.reload();

    // Clear unsaved goals
    Goals.clearApiCache();

    onClose();
  }

  async function handlePostSave(): Promise<void> {
    await onPostSave();
    await handleClose();
  }

  return (
    <Modal
      allowOverflow={true}
      closeOnClickOutside={false}
      description={t(
        'These intelligent recommendations may increase the performance of this campaign.',
      )}
      onClose={handleClose}
      opened={opened}
      rightActions={
        <>
          <Button onClick={handleClose} type={'secondary'}>
            {t('Cancel')}
          </Button>
          <SaveCampaignButton
            campaign={campaign}
            childModels={childModels}
            grandchildModels={grandchildModels}
            postSave={handlePostSave}
            showIcon={true}
            type={'primary'}
            validate={getValidationErrors}
          />
        </>
      }
      size={type === ERecommendationType.MissingGoal ? 'md' : 'default'}
      title={t('Recommended campaign optimization')}
    >
      <Stack>
        {recommendation && (
          <>
            {type === ERecommendationType.ExtendCampaign && (
              <ExtendEndDate
                campaign={campaign}
                errors={errors?.duration ?? []}
                recommendation={recommendation}
              />
            )}

            {type === ERecommendationType.Budget && (
              <IncreaseBudget
                campaign={campaign}
                errors={errors?.budget ?? []}
                recommendation={recommendation}
              />
            )}

            {type === ERecommendationType.BudgetDuration && (
              <IncreaseBudgetExtendEndDate
                campaign={campaign}
                errors={errors}
                recommendation={recommendation}
              />
            )}

            {type === ERecommendationType.IncreaseBaseBid && (
              <IncreaseBaseBid campaign={campaign} />
            )}

            {type === ERecommendationType.IncreaseFrequencyCap && (
              <IncreaseFrequencyCap campaign={campaign} />
            )}
            {type === ERecommendationType.MissingGoal && <AddGoal campaign={campaign} />}

            {type === ERecommendationType.EnableOmnitargeting && (
              <EnableOmnitargeting campaign={campaign} />
            )}
          </>
        )}
      </Stack>
    </Modal>
  );
}

RecommendationModal.Alert = RecommendationAlert;

export default observer(RecommendationModal);
